import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Circle, Square, X } from "lucide-react";
import { Ambiance } from "../../domain/types";
import { AMBIANCE_FLOOR, AMBIANCES } from "../../application/boardDefaults";

type Props = {
    isOpen: boolean;
    onClose: () => void;
    shape: "circle" | "square";
    onSetShape: (s: "circle" | "square") => void;
    color: string;
    onSetColor: (c: string) => void;
    ambiance: Ambiance;
    onSetAmbiance: (a: Ambiance) => void;
    portalContainer?: HTMLElement | null;
};

/** Board styling modal (therapist): table shape, floor color and the scene /
 *  panorama (day / night / rain). Self-portals so it shows over the immersive
 *  and OS-fullscreen board (issue #197). */
export default function BoardStyleModal({
    isOpen,
    onClose,
    shape,
    onSetShape,
    color,
    onSetColor,
    ambiance,
    onSetAmbiance,
    portalContainer,
}: Props) {
    const { t } = useTranslation();
    if (!isOpen) return null;

    const shapeBtn = (value: "circle" | "square", icon: React.ReactNode, text: string) => (
        <button
            type="button"
            onClick={() => onSetShape(value)}
            aria-pressed={shape === value}
            className={`flex min-h-11 flex-1 items-center justify-center gap-1.5 rounded-md border px-3 text-sm font-medium transition-colors duration-[--duration-fast] ${
                shape === value
                    ? "border-accent bg-accent-tint text-accent-on-tint"
                    : "border-border-strong text-content-secondary hover:border-accent hover:text-content-primary"
            }`}
        >
            {icon} {text}
        </button>
    );

    return createPortal(
        <div
            className="fixed inset-0 z-[80] flex items-end justify-center bg-gray-950/50 backdrop-blur-sm sm:items-center"
            onClick={onClose}
        >
            <div
                className="w-full max-w-sm rounded-t-2xl border border-border-subtle bg-surface-overlay p-6 shadow-modal sm:rounded-2xl"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-label={t("constellation.boardStyle.title")}
            >
                <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-base font-semibold text-content-primary">
                        {t("constellation.boardStyle.title")}
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t("common.close", "Close")}
                        className="flex size-11 items-center justify-center rounded-md text-content-secondary transition-colors duration-[--duration-fast] hover:bg-surface-sunken hover:text-content-primary"
                    >
                        <X size={16} />
                    </button>
                </div>

                <p className="mb-1 text-xs font-semibold uppercase tracking-[0.08em] text-content-secondary">
                    {t("constellation.table.shape")}
                </p>
                <div className="mb-4 flex gap-2">
                    {shapeBtn("circle", <Circle size={16} />, t("constellation.table.circle"))}
                    {shapeBtn("square", <Square size={16} />, t("constellation.table.square"))}
                </div>

                <div className="mb-4 flex items-center justify-between">
                    <p className="text-xs font-semibold uppercase tracking-[0.08em] text-content-secondary">
                        {t("constellation.table.color")}
                    </p>
                    <input
                        type="color"
                        value={color}
                        onChange={(e) => onSetColor(e.target.value)}
                        aria-label={t("constellation.table.color")}
                        className="h-11 w-14 cursor-pointer rounded-md border border-border-strong bg-surface-raised"
                    />
                </div>

                <p className="mb-1 text-xs font-semibold uppercase tracking-[0.08em] text-content-secondary">
                    {t("constellation.ambiance.label")}
                </p>
                <select
                    value={ambiance}
                    onChange={(e) => {
                        const next = e.target.value as Ambiance;
                        onSetAmbiance(next);
                        const floor = AMBIANCE_FLOOR[next];
                        if (floor) onSetColor(floor);
                    }}
                    aria-label={t("constellation.ambiance.label")}
                    className="field-base field-default field-height"
                >
                    {AMBIANCES.map((a) => (
                        <option key={a} value={a}>
                            {t(`constellation.ambiance.${a}`)}
                        </option>
                    ))}
                </select>

                <div className="mt-5 flex justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        className="min-h-11 rounded-lg bg-accent px-4 py-2.5 text-sm font-semibold text-accent-contrast transition-colors duration-[--duration-fast] hover:bg-accent-hover"
                    >
                        {t("common.done", "Done")}
                    </button>
                </div>
            </div>
        </div>,
        portalContainer ?? document.body,
    );
}
